import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const OTP_LENGTH = 4;
const RESEND_INTERVAL = 30;
const MOCK_JWT =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjE4OTM0NTYwMDB9.mocksignature";

const MobileOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otpValues, setOtpValues] = useState(Array(OTP_LENGTH).fill(""));
  const [timer, setTimer] = useState(RESEND_INTERVAL);
  const inputRefs = useRef([]);

  const maskedPhone = location.state?.phone || "+91 XXXX XXXX99";

  useEffect(() => {
    if (timer === 0) return undefined;
    const interval = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return;
    const updated = [...otpValues];
    updated[index] = value;
    setOtpValues(updated);
    if (value && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otpValues[index] && index > 0) {
      const updated = [...otpValues];
      updated[index - 1] = "";
      setOtpValues(updated);
      inputRefs.current[index - 1]?.focus();
      event.preventDefault();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    // Mock verification hook-up so QA can access onboarding flow.
    const mockUserId = `mobile_${Date.now()}`;
    localStorage.setItem("userId", mockUserId);
    localStorage.setItem("token", MOCK_JWT);
    localStorage.setItem(
      "userToken",
      JSON.stringify({
        userId: mockUserId,
        phone: maskedPhone,
        fullName: "Mobile User",
      })
    );
    localStorage.setItem("profileUpdated", "false");
    localStorage.setItem("onboardingComplete", "false");
    navigate("/onboarding");
  };

  const handleResend = () => {
    if (timer > 0) return;
    setOtpValues(Array(OTP_LENGTH).fill(""));
    setTimer(RESEND_INTERVAL);
    inputRefs.current[0]?.focus();
  };

  const isOtpComplete = otpValues.every((digit) => digit.length === 1);

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-12 bg-primary overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -right-16 h-72 w-72 bg-white/10 blur-[140px]"></div>
        <div className="absolute -bottom-20 -left-20 h-80 w-80 bg-accent/20 blur-[150px]"></div>
        <div className="absolute inset-x-0 top-1/2 h-px bg-white/10"></div>
      </div>

      <div className="relative w-full max-w-md">
        <div className="bg-white/95 rounded-[32px] p-8 shadow-[0_35px_120px_rgba(15,23,42,0.55)] border border-white/40 flex flex-col items-center gap-6 text-center">
          <div className="h-20 w-20 rounded-3xl bg-white shadow-lg ring-1 ring-slate-100 overflow-hidden flex items-center justify-center">
            <img
              src="/assets/images/robot.png"
              alt="Aanya mascot"
              className="h-full w-full object-contain"
            />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-extrabold text-slate-900">
              Enter your verification code
            </h1>
            <p className="text-sm text-slate-500">
              Sent to <span className="font-semibold text-slate-700">{maskedPhone}</span>
            </p>
          </div>

          <form className="w-full space-y-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-4 gap-4">
              {otpValues.map((digit, index) => (
                <input
                  key={`otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(event) => handleChange(index, event.target.value.trim())}
                  onKeyDown={(event) => handleKeyDown(index, event)}
                  ref={(element) => {
                    inputRefs.current[index] = element;
                  }}
                  className="h-16 rounded-2xl border border-slate-200 text-center text-2xl font-semibold tracking-widest text-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              ))}
            </div>

            <div className="text-sm text-slate-500">
              {timer > 0 ? (
                <>
                  Resend code in{" "}
                  <span className="font-semibold text-slate-700">
                    00:{timer.toString().padStart(2, "0")}
                  </span>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  className="font-semibold text-indigo-600 hover:text-indigo-700"
                >
                  Resend code
                </button>
              )}
            </div>

            <button
              type="submit"
              className="w-full rounded-md bg-primary py-3 text-white text-lg font-semibold shadow-lg shadow-primary/30 hover:brightness-105 transition disabled:opacity-60"
              disabled={!isOtpComplete}
            >
              Verify &amp; Start
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MobileOtp;
